/**
 * Frontend Application Insights Telemetry
 *
 * Provides client-side telemetry for:
 * - Page views (automatic via React Router integration)
 * - UI exceptions (integrates with ErrorBoundary)
 * - Custom events (AI chat usage, report generation, etc.)
 *
 * The connection string is injected via VITE_APPINSIGHTS_CONNECTION_STRING
 * environment variable. If not set, telemetry calls are silent no-ops.
 */

import { ApplicationInsights, DistributedTracingModes } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights | null = null;

/**
 * Initialize Application Insights for the frontend.
 * Call once in main.tsx before rendering.
 */
export function initFrontendTelemetry(): void {
  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;

  if (!connectionString) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info('[Telemetry] VITE_APPINSIGHTS_CONNECTION_STRING not set – telemetry disabled');
    }
    return;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        disableFetchTracking: false,
        enableCorsCorrelation: true,
        // W3C-only: send traceparent, not the legacy Request-Id header.
        // Request-Id is not in the API's CORS allow-list; sending it fails
        // the preflight and breaks every instrumented cross-origin call.
        distributedTracingMode: DistributedTracingModes.W3C,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        disableExceptionTracking: false,
        maxBatchInterval: 5000,
      },
    });

    appInsights.loadAppInsights();
    appInsights.trackPageView();

    // eslint-disable-next-line no-console
    console.info('[Telemetry] Application Insights initialized');
  } catch (error) {
    console.error('[Telemetry] Failed to initialize Application Insights:', error);
    appInsights = null;
  }
}

/**
 * Track a custom event
 */
export function trackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>,
): void {
  appInsights?.trackEvent({ name, properties, measurements });
}

/**
 * Track an exception (use in ErrorBoundary or catch blocks)
 */
export function trackException(error: Error, properties?: Record<string, string>): void {
  appInsights?.trackException({ exception: error, properties });
}

/**
 * Track a metric
 */
export function trackMetric(
  name: string,
  average: number,
  properties?: Record<string, string>,
): void {
  appInsights?.trackMetric({ name, average, properties });
}

/**
 * Set the authenticated user context (call after login)
 */
export function setAuthenticatedUser(userId: string, accountId?: string): void {
  appInsights?.setAuthenticatedUserContext(userId, accountId, true);
}

/**
 * Clear the authenticated user context (call after logout)
 */
export function clearAuthenticatedUser(): void {
  appInsights?.clearAuthenticatedUserContext();
}

/**
 * Get the underlying Application Insights instance (for advanced usage)
 */
export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}
